import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { CloseSmall } from '@wix/wix-ui-icons-common/on-stage';
import { MobileDrawer } from '@wix/wix-ui-tpa/cssVars';
import { getInternalLinkRoute } from '@wix/communities-blog-client-common';
import { useKeyPress } from '../../hooks/use-key-press';
import { getActiveLink, getFeedAndCategoryLinks } from '../../selectors/category-links-selectors';
import { isSeo as getIsSeo } from '../../store/basic-params/basic-params-selectors';
import { getTopology } from '../../store/topology/topology-selectors';
import { ArrowDownIcon } from '../icons/arrow-down-icon';
import { useSelector } from '../runtime-context';
import SeoSelect from './seo-select';
import styles from './category-drawer.scss';

const CategoryDrawer: React.FC = () => {
  const { isPreview } = useEnvironment();
  const links = useSelector(getFeedAndCategoryLinks);
  const activeCategoryLink = useSelector(getActiveLink);
  const isSeo = useSelector(getIsSeo);
  const topology = useSelector(getTopology);
  const listRef = useRef<HTMLUListElement>(null);
  const navigationRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const handleToggleClick = useCallback(() => {
    if (isPreview) {
      // MobileDrawer backdrop gets out-of-bounds when open in preview mode on Mobile
      return;
    }
    setIsOpen((prev) => !prev);
  }, [isPreview]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useKeyPress('Escape', handleClose);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (listRef.current && navigationRef.current && isOpen) {
        const activeLink =
          listRef.current.querySelector<HTMLAnchorElement>('[aria-current="page"]');
        if (activeLink) {
          navigationRef.current.scrollTo({
            top:
              activeLink.offsetTop -
              navigationRef.current.clientHeight / 2 +
              activeLink.clientHeight / 2,
          });
          activeLink.focus({ preventScroll: true });
        }
      }
    });
  }, [isOpen]);

  if (isSeo) {
    return <SeoSelect />;
  }

  return (
    <div className={styles.root}>
      <button
        data-hook="category-dropdown"
        type="button"
        onClick={handleToggleClick}
        aria-label={t('category-change.select-name')}
        className={styles.trigger}
      >
        <span className={styles.ellipsis} data-hook="category-title">
          {activeCategoryLink.text}
        </span>
        <ArrowDownIcon className={styles.fillCurrentColor} />
      </button>
      <MobileDrawer appendToBody isOpen={isOpen} onRequestClose={handleClose}>
        <nav ref={navigationRef} className={styles.nav}>
          <ul ref={listRef}>
            {links.map((link) => {
              const isActive = link.path === activeCategoryLink.path;
              const href = getInternalLinkRoute({
                topology,
                to: link.path,
                shouldNavigateToFeed: true,
              });

              return (
                <li key={link.key}>
                  <a
                    href={href}
                    aria-current={isActive ? 'page' : undefined}
                    className={styles.link}
                    onClick={handleClose}
                  >
                    {link.text}
                  </a>
                </li>
              );
            })}
            <li className={styles.itemSticky}>
              <button type="button" onClick={handleClose} className={styles.link}>
                <CloseSmall /> {t('category-change.aria-label.close')}
              </button>
            </li>
          </ul>
        </nav>
      </MobileDrawer>
    </div>
  );
};

export default CategoryDrawer;
